// B2C portal analytics beacon → POST /journey/events/ (authenticated).
//
// Batches candidate-portal engagement events (video/reel/report views, auth)
// and flushes them to the Django journey-event ingest, which persists a durable
// JourneyEvent row (source of truth for the funnel + triggers) and mirrors to
// App Insights.
//
// Deliberately SEPARATE from the interview-room beacon
// (components/voice-room/lib/events.ts): that one is an AllowAny, meeting-keyed
// beacon that can use navigator.sendBeacon (no auth header). This one is
// authenticated and uid-keyed, so it cannot use sendBeacon — it flushes with a
// keepalive fetch carrying the Firebase token, falling back to a cached token
// on pagehide. Keeping the two beacons independent avoids any shared-state
// cross-contamination between the (unauth) interview room and the (authed)
// portal, and leaves the live voice-room beacon untouched.

import { API_URL } from "@/components/voice-room/lib/config";
import { auth } from "@/components/context/firebase";
import { mpTrack } from "@/lib/analytics/mixpanel";
import { EVENTS } from "@/lib/analytics/events";
import { trackWebsiteAction } from "@/lib/analytics/website";
import {
  hasAnalyticsConsent,
  subscribeToAnalyticsConsent,
} from "@/lib/analytics/consent";

type EventProps = Record<string, unknown>;

interface QueuedEvent {
  name: string;
  t: number;
  seq: number;
  props?: EventProps;
}

const FLUSH_INTERVAL_MS = 10_000;
const MAX_QUEUE = 200;
const MAX_BATCH = 50;
const WEBSITE_PRODUCT_EVENTS = new Set<string>(Object.values(EVENTS));

// Dev parity with fetchWithAuth.js: with Firebase bypassed locally, identify
// the user to the backend via X-Dev-Uid (paired with backend DEV_AUTH_BYPASS).
const DISABLE_AUTH =
  String(process.env.NEXT_PUBLIC_DISABLE_AUTH || "").toLowerCase() === "true";
const DEBUG_UID = process.env.NEXT_PUBLIC_DEBUG_UID || "debug-local-hr";

let uid = "";
let sessionId = "";
let seq = 0;
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let cachedToken = "";
let consentGeneration = 0;

const onVisibilityChange = (): void => {
  if (document.visibilityState === "hidden") flush(true);
};
const onPageHide = (): void => flush(true);

const endpoint = (): string => `${API_URL}/journey/events/`;

const serialize = (events: QueuedEvent[]): string =>
  JSON.stringify({ sessionId, src: "client", events });

const authHeaders = async (): Promise<Record<string, string> | null> => {
  if (DISABLE_AUTH) return { "X-Dev-Uid": uid || DEBUG_UID };
  try {
    const user = auth?.currentUser;
    if (!user) return null;
    const token = await user.getIdToken();
    cachedToken = token; // warm the cache so a pagehide flush can authenticate
    return { Authorization: `Bearer ${token}` };
  } catch {
    return null;
  }
};

const post = (
  body: string,
  headers: Record<string, string>,
  onFail?: () => void
): void => {
  fetch(endpoint(), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body,
    keepalive: true,
  })
    .then((res) => {
      // 5xx is transient (MSSQL cold-start, nginx reload) — keep the batch.
      if (res.status >= 500 && onFail) onFail();
    })
    .catch(() => {
      if (onFail) onFail();
    });
};

const flush = (useUnloadPath = false): void => {
  if (!hasAnalyticsConsent()) return;
  // Identity is NOT required to queue — only to send. authHeaders() below holds
  // the batch (requeue) when Firebase identity isn't available yet, so pre-auth
  // events drain the moment the user is known instead of being dropped.
  if (queue.length === 0) return;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(batch.length);
  const body = serialize(batch);
  const generation = consentGeneration;
  const requeue = () => {
    if (!hasAnalyticsConsent() || generation !== consentGeneration) return;
    queue = batch.concat(queue).slice(0, MAX_QUEUE);
  };

  if (useUnloadPath) {
    // The page is unloading — we can't await a fresh token. Use the cached one
    // (or the dev header) for a keepalive fetch; drop the trailing batch if we
    // have neither. Portal events that matter fire mid-session, not at unload.
    if (DISABLE_AUTH) {
      post(body, { "X-Dev-Uid": uid || DEBUG_UID });
    } else if (cachedToken) {
      post(body, { Authorization: `Bearer ${cachedToken}` });
    }
    return;
  }

  authHeaders().then((headers) => {
    if (!hasAnalyticsConsent() || generation !== consentGeneration) return;
    if (!headers) {
      requeue();
      return;
    }
    post(body, headers, requeue);
  });
};

/** Emit one portal event. Queued immediately and flushed once Firebase identity
 *  is available — the queue SURVIVES the pre-auth window so a trigger-grade event
 *  fired before userProfile loads is never silently dropped. */
export const emitJourneyEvent = (name: string, props?: EventProps): void => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  // Mirror to Mixpanel (product analytics). Best-effort and self-guarded — the
  // journey beacon below stays the source of truth for the funnel + triggers.
  mpTrack(name, props);
  const audience = props?.role === "hr" || props?.role === "candidate"
    ? props.role
    : undefined;
  if (name === EVENTS.AUTH_SIGNUP_COMPLETE) {
    trackWebsiteAction("signup_completed", audience ? { audience } : {});
  } else if (name === EVENTS.AUTH_LOGIN_SUCCESS) {
    trackWebsiteAction("login_completed", audience ? { audience } : {});
  } else if (WEBSITE_PRODUCT_EVENTS.has(name)) {
    trackWebsiteAction("product_event", {
      target: name,
      ...(audience ? { audience } : {}),
    });
  }
  if (!sessionId) sessionId = Math.random().toString(36).slice(2, 10);
  seq += 1;
  const ev: QueuedEvent = { name, t: Date.now(), seq };
  if (props && Object.keys(props).length > 0) ev.props = props;
  queue.push(ev);
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
};

/** Install the beacon for a known user. Idempotent; safe to call on every
 *  render. Pass the uid explicitly right after a Firebase sign-in so auth
 *  events attribute before the React profile context catches up. */
export const setupJourneyAnalytics = (identity: { uid?: string }): void => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  const nextUid = identity.uid || "";
  // Only ever SET the uid (never clear it on a transient empty userProfile) and
  // warm the token cache when it changes.
  if (nextUid && nextUid !== uid) {
    uid = nextUid;
    void authHeaders();
  }
  if (!sessionId) sessionId = Math.random().toString(36).slice(2, 10);
  // Install the flush loop UNCONDITIONALLY (even pre-login) so a queue that
  // filled before identity was known drains the instant Firebase resolves.
  if (!flushTimer) {
    flushTimer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
  }
};

const stopJourneyAnalytics = (): void => {
  consentGeneration += 1;
  queue = [];
  uid = "";
  sessionId = "";
  seq = 0;
  cachedToken = "";
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
  }
};

if (typeof window !== "undefined") {
  subscribeToAnalyticsConsent((consent) => {
    if (consent === "rejected") stopJourneyAnalytics();
  });
}
